import React, { memo, RefObject, KeyboardEvent, ChangeEvent } from "react";
import { Box, Paper, TextField, IconButton, List, ListItemButton, ListItemText, CircularProgress, Tooltip } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import MicIcon from "@mui/icons-material/Mic";
import { PropertyOption } from "../../../../../shared/types/chatWindow.types";

interface ChatInputAreaProps {
    input: string;
    status: "idle" | "thinking" | "typing";
    showMentions: boolean;
    isLoadingProps: boolean;
    filteredProperties: PropertyOption[];
    isMobile: boolean;
    isMaximized: boolean;
    inputRef: RefObject<HTMLInputElement>;
    onInputChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onSelectProperty: (property: PropertyOption) => void;
    onSend: () => void;
    onStop: () => void;
    isRecording?: boolean;
    isTranscribing?: boolean;
    onStartRecording?: () => void;
    onStopRecording?: () => void;
}

const ChatInputArea: React.FC<ChatInputAreaProps> = memo(({
    input,
    status,
    showMentions,
    isLoadingProps,
    filteredProperties,
    isMobile,
    isMaximized,
    inputRef,
    onInputChange,
    onSelectProperty,
    onSend,
    onStop,
    isRecording = false,
    isTranscribing = false,
    onStartRecording,
    onStopRecording,
}) => {
    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (showMentions && filteredProperties.length > 0) return;
            if (input.trim() && status === "idle" && !isRecording && !isTranscribing) onSend();
        }
    };

    return (
        <Box sx={{ position: "relative", p: 1.5, bgcolor: "#e4edef", borderTop: "1px solid #5FB7CF" }}>
            {!isMobile && !isMaximized && (
                <Box
                    sx={{
                        position: "absolute",
                        bottom: 2,
                        right: 2,
                        width: 14,
                        height: 14,
                        cursor: "nwse-resize",
                        backgroundImage: "linear-gradient(135deg, transparent 50%, #5FB7CF 50%)",
                        opacity: 0.6,
                        borderRadius: "0 0 16px 0",
                        zIndex: 10,
                    }}
                />
            )}

            {showMentions && (
                <Paper
                    elevation={4}
                    sx={{
                        position: "absolute",
                        padding: "2px",
                        bottom: "100%",
                        left: 20,
                        width: "250px",
                        maxHeight: "200px",
                        overflowY: "auto",
                        mb: 1,
                        borderRadius: "12px",
                        zIndex: 10,
                    }}
                >
                    {isLoadingProps ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                            <CircularProgress size={20} />
                        </Box>
                    ) : filteredProperties.length > 0 ? (
                        <List dense disablePadding>
                            {filteredProperties.map((prop) => (
                                <ListItemButton
                                    key={prop.code}
                                    onClick={() => onSelectProperty(prop)}
                                    sx={{ "&:hover": { bgcolor: "#f1f5f9" } }}
                                >
                                    <ListItemText
                                        primary={prop.name}
                                        secondary={prop.code}
                                        primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: 500 }}
                                        secondaryTypographyProps={{ fontSize: "0.75rem" }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    ) : (
                        <Box sx={{ p: 2, color: "#64748b", fontSize: "0.85rem" }}>No properties found</Box>
                    )}
                </Paper>
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={
                        isRecording
                            ? "Listening..."
                            : isTranscribing
                            ? "Transcribing audio..."
                            : "Enter your message... (@ to link property)"
                    }
                    value={input}
                    onChange={onInputChange}
                    inputRef={inputRef}
                    onKeyDown={handleKeyDown}
                    disabled={isTranscribing}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "24px",
                            bgcolor: "white",
                            height: "50px",
                            fontSize: "0.9rem",
                            "& fieldset": { borderColor: "#cbd5e1" },
                            "&:hover fieldset": { borderColor: "#a1d2df" },
                            "&.Mui-focused fieldset": { borderColor: "#a1d2df" },
                        },
                    }}
                    InputProps={{
                        endAdornment: (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: -0.5 }}>
                                {/* Microphone / Transcribing Button */}
                                {isTranscribing ? (
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36 }}>
                                        <CircularProgress size={20} sx={{ color: "#5FB7CF" }} />
                                    </Box>
                                ) : isRecording ? (
                                    <Tooltip title="Stop recording">
                                        <IconButton
                                            onClick={onStopRecording}
                                            sx={{
                                                color: "#ffffff",
                                                bgcolor: "#ef4444",
                                                width: 36,
                                                height: 36,
                                                animation: "pulse 1.5s infinite",
                                                "@keyframes pulse": {
                                                    "0%": { boxShadow: "0 0 0 0 rgba(239, 68, 68, 0.4)" },
                                                    "70%": { boxShadow: "0 0 0 8px rgba(239, 68, 68, 0)" },
                                                    "100%": { boxShadow: "0 0 0 0 rgba(239, 68, 68, 0)" },
                                                },
                                                "&:hover": { bgcolor: "#dc2626" },
                                            }}
                                        >
                                            <StopIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Voice input">
                                        <span>
                                            <IconButton
                                                onClick={onStartRecording}
                                                disabled={status !== "idle"}
                                                sx={{
                                                    color: "#64748b",
                                                    width: 36,
                                                    height: 36,
                                                    "&:hover": { color: "#5FB7CF", bgcolor: "#f1f5f9" },
                                                    "&.Mui-disabled": { color: "#cbd5e1" },
                                                }}
                                            >
                                                <MicIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}

                                {/* Send / Stop Generation Button */}
                                {status !== "idle" ? (
                                    <IconButton
                                        onClick={onStop}
                                        sx={{
                                            color: "#9e9e9e",
                                            bgcolor: "#e0e0e0",
                                            width: 36,
                                            height: 36,
                                            borderRadius: "50%",
                                            "&:hover": { bgcolor: "#073857", color: "#5fb7cf" },
                                        }}
                                    >
                                        <StopIcon fontSize="small" />
                                    </IconButton>
                                ) : (
                                    <IconButton
                                        onClick={onSend}
                                        disabled={!input.trim() || isRecording || isTranscribing}
                                        sx={{
                                            bgcolor: "#073857",
                                            color: "#5fb7cf",
                                            width: 36,
                                            height: 36,
                                            borderRadius: "50%",
                                            "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#9e9e9e" },
                                        }}
                                    >
                                        <SendIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        ),
                    }}
                />
            </Box>
        </Box>
    );
});

export default ChatInputArea;